import React from 'react';
import { useTranslation } from 'react-i18next';

import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import copyMediaElementOperation from 'editor/src/store/design/operation/copyMediaElementOperation';
import getMediaElement from 'editor/src/store/design/selector/getMediaElement';
import isAddElementsAllowed from 'editor/src/store/editor/selector/isAddElementsAllowed';
import saveUndoRedoStateOperation from 'editor/src/store/editorModules/undoRedo/operation/saveUndoRedoStateOperation';
import { useDispatch, useStore } from 'editor/src/store/hooks';

import { logEvent } from 'editor/src/amplitude';

import useCustomToastr from 'editor/src/component/CustomToastrComponent/useCustomToastr';
import IconApplyToAllAreas from 'editor/src/component/Icon/IconApplyToAllAreas';
import { CanShow, MenuItemProps } from 'editor/src/component/Menu/type';

import MenuButton from './MenuButton';

// Hardcoded print area conflict rules
// TODO: Move this to product configuration in the future
const PRINT_AREA_CONFLICTS: { [key: string]: string[] } = {
  // T-shirt conflicts
  back: ['neck-outer'],
  'neck-inner': ['neck-outer'],
  'neck-outer': ['back', 'neck-inner'],
  // Embroidery conflicts
  'chest-center-embroidery': ['chest-large-embroidery', 'chest-left-embroidery'],
  'chest-large-embroidery': ['chest-center-embroidery', 'chest-left-embroidery'],
  'chest-left-embroidery': ['chest-center-embroidery', 'chest-large-embroidery'],
  // Add more conflict rules as discovered
};

export const canShow: CanShow = (state, { hasSelection, isElementEmpty, structureIndexes }) => {
  if (!hasSelection || isElementEmpty || structureIndexes.length !== 1 || !isAddElementsAllowed(state)) {
    return false;
  }
  const element = getMediaElement(state, structureIndexes[0]);
  if (!element || element.unduplicable) {
    return false;
  }

  // Check if there are multiple pages across spreads
  const spreads = state.design.designData?.spreads || [];
  let totalPages = 0;
  spreads.forEach((spread) => {
    totalPages += spread.pages.length;
  });

  return totalPages > 1;
};

type Props = Pick<MenuItemProps, 'context' | 'mode' | 'close'>;

function ButtonApplyToAllPrintAreas({ context, mode, close }: Props) {
  const dispatch = useDispatch();
  const store = useStore();
  const { t } = useTranslation();
  const showToastr = useCustomToastr();

  const handleClick = () => {
    const state = store.getState();
    const spreads = state.design.designData?.spreads || [];
    const structureIndex = context.structureIndexes[0];
    const currentSpreadIndex = structureIndex.spreadIndex;
    const currentPageIndex = structureIndex.pageIndex;

    // First, identify all print areas that currently have media elements
    const occupiedPrintAreas = new Set<string>();
    spreads.forEach((spread) => {
      spread.pages.forEach((page) => {
        const hasExistingMedia = page.groups.media && page.groups.media.length > 0;
        if (hasExistingMedia) {
          // Normalize spread name to lowercase with hyphens for conflict matching
          const normalizedName = spread.name.toLowerCase().replace(/\s+/g, '-');
          occupiedPrintAreas.add(normalizedName);
        }
      });
    });

    // Collect all valid print areas (skip ones with conflicts)
    const destinations: Array<{ spreadIndex: number; pageIndex: number }> = [];

    spreads.forEach((spread, spreadIndex) => {
      spread.pages.forEach((page, pageIndex) => {
        // Skip the current page
        if (spreadIndex === currentSpreadIndex && pageIndex === currentPageIndex) {
          return;
        }

        // Check if this page is editable (has mediabox)
        if (page.groups.mediabox && page.groups.mediabox.length > 0) {
          // Check for conflicts with occupied print areas (including ones we're about to add)
          const normalizedName = spread.name.toLowerCase().replace(/\s+/g, '-');
          const conflicts = PRINT_AREA_CONFLICTS[normalizedName] || [];

          const hasConflict = conflicts.some((conflictingArea) => occupiedPrintAreas.has(conflictingArea));

          if (!hasConflict) {
            destinations.push({ spreadIndex, pageIndex });
            // Mark this area as occupied so subsequent conflict checks see it
            occupiedPrintAreas.add(normalizedName);
          }
        }
      });
    });

    // Copy to all valid print areas at once
    if (destinations.length > 0) {
      batch(() => {
        dispatch(saveUndoRedoStateOperation('Apply to all print areas'));

        destinations.forEach((destination) => {
          dispatch(copyMediaElementOperation(structureIndex, destination, false));
        });
      });

      // Track the action
      logEvent('apply to all print areas', { originSpreadName: spreads[currentSpreadIndex].name });

      // Show success notification
      const message = t('editor-apply-to-all-print-areas-success');
      showToastr('success', message);
    } else {
      // Show info if no valid destinations
      showToastr('info', t('editor-apply-to-all-print-areas-no-destinations'));
    }

    close?.();
  };

  return (
    <MenuButton
      Icon={IconApplyToAllAreas}
      mode={mode}
      handleClick={handleClick}
      i18nKey="editor-apply-to-all-print-areas"
      className="cy-button-apply-to-all-print-areas"
    />
  );
}

export default React.memo(ButtonApplyToAllPrintAreas);
